import { devLog, alwaysLog } from '@/utils/logger';
import { createApp, watch, ref } from 'vue';
import { createPinia } from 'pinia';
import 'element-plus/es/components/loading/style/css';
import ElementPlus, { ElLoading } from 'element-plus';
import 'element-plus/dist/index.css';
import App from './App.vue';
import './assets/main.css';
import '@/assets/iconfont/iconfont.css';
import 'element-plus/es/components/message/style/css';
import I18nPlugin from '@/i18n';

import installIcon from '@/plugins/installIcon'; // icon 注册
import { router } from '@/plugins/installRouter'; // 路由注册
import { mediaCacheService } from '@/services/MediaCacheService';
import { googleAuthService } from '@/services/GoogleAuthService';
import {
  postTokenToDriveStreamSw,
  registerDriveStreamServiceWorker
} from '@/services/driveStreamSwBridge';
import { beginMediaReconnectHint, endMediaReconnectHint } from '@/services/mediaReconnectHint';
import { showRecoveryMaskIfPending } from '@/services/mediaRecoveryService';
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const app = createApp(App);
// app.config.globalProperties.$showLoading = ref(false);
// app.config.globalProperties.$ElLoading = ElLoading.service({
//     text: '核心加载中...'
// });

const pinia = createPinia();

app.use(pinia);
app.use(router);
app.use(installIcon);
app.use(ElementPlus);
app.use(I18nPlugin);
app.use(VueVirtualScroller);

// 媒体恢复跨页刷新：Element Plus 就绪后立刻盖蒙版，防止用户在重组完成前操作
showRecoveryMaskIfPending();

// 添加全局错误处理
app.config.errorHandler = (err, instance, info) => {
  alwaysLog.error('[Vue Error]', {
    error: err,
    component: instance?.$options?.name || 'Unknown',
    info,
    stack: err instanceof Error ? err.stack : undefined
  });
  
  // 如果是组件更新相关的错误，尝试恢复
  if (err instanceof Error && err.message.includes('emitsOptions')) {
    devLog.warn('[Vue Error] 检测到组件更新错误，尝试恢复...');
    // 可以在这里添加恢复逻辑
  }
};

// 修改为峰值数据缓存
app.provide('waveformDataCache', ref(new Map()));
// 视频缩略图缓存
app.provide('videoThumbnailCache', ref(new Map()));

// 启动缓存恢复服务
devLog.log('[App] 启动缓存恢复服务...');
mediaCacheService.quickRecovery().then(stats => {
  devLog.log('[App] 缓存恢复完成:', stats);
});

app.mount('#vite-app');

// Google Drive 流式媒体：注册 SW，同步 token，处理 SW 侧 401 触发的静默刷新
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  const syncStreamClient = () => {
    const t = googleAuthService.getAccessToken();
    if (t) postTokenToDriveStreamSw(t);
  };

  registerDriveStreamServiceWorker()
    .then(() => {
      syncStreamClient();
    })
    .catch(err => alwaysLog.error('[DriveStreamSW] 初始化失败', err));

  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data?.type === 'TOKEN_REFRESH_NEEDED') {
      beginMediaReconnectHint();
      (async() => {
        const ok = await googleAuthService.refreshAccessTokenSilently();
        if (ok) {
          const t = googleAuthService.getAccessToken();
          if (t) postTokenToDriveStreamSw(t);
          return;
        }
        await googleAuthService.recoverSessionAfterUnauthorized();
        const t2 = googleAuthService.getAccessToken();
        if (t2) postTokenToDriveStreamSw(t2);
      })()
        .catch(err => alwaysLog.error('[DriveStreamSW] TOKEN_REFRESH_NEEDED 处理失败', err))
        .finally(() => {
          endMediaReconnectHint();
        });
    }
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    syncStreamClient();
  });

  // 主动续期：默认 access_token ~1h，提前 15min 刷新，避免挂机后首个 Range 请求 401 → MediaError
  const TOKEN_PROACTIVE_REFRESH_MS = 45 * 60 * 1000;
  const proactiveRefreshToken = () => {
    (async() => {
      try {
        if (!googleAuthService.getAccessToken()) return;
        const ok = await googleAuthService.refreshAccessTokenSilently();
        if (ok) {
          const t = googleAuthService.getAccessToken();
          if (t) postTokenToDriveStreamSw(t);
          devLog.log('[DriveStreamSW] 主动 token 续期完成');
        }
      } catch (err) {
        alwaysLog.warn('[DriveStreamSW] 主动 token 续期失败', err);
      }
    })().catch(() => undefined);
  };
  window.setInterval(proactiveRefreshToken, TOKEN_PROACTIVE_REFRESH_MS);
}
